import { useDisplayMedia } from '@vueuse/core';
import { watch, computed, ref } from 'vue';

import { defineStore } from 'pinia';

import { useMicAnalyzer } from '@/composables/shared/mic/create/useMicAnalyzer';
import { useMicEffect } from '@/composables/shared/mic/create/useMicEffect';
import { useMicGraph } from '@/composables/shared/mic/create/useMicGraph';
import { i18n } from '@/plugins/i18n';
import { useToastStore } from '@/stores/toast.js';
import { SystemAudioMicrophoneStoreOptions, MicProcessEvents } from '@/types/microphone';
import {
  DEFAULT_CLIPPING_CONFIG,
  DEFAULT_NOISE_FLOOR_CONFIG,
  MIC_INITIAL_GAIN,
  VOLUME_RATIO,
} from '@/utils/constants/microphone';
import { getBrowserAndDeviceInfo } from '@/utils/device';
import truncateToDecimal from '@/utils/truncateToDecimal';
import runQueue from '@/utils/useQueue';

export const useSystemAudioMicrophoneStore = defineStore('systemAudioMicrophone', () => {
  const { t } = i18n.global;

  const toastStore = useToastStore();

  const micGraphRenderQueue = runQueue();
  const { micGraph, setupMicGraph, teardownMicGraph } = useMicGraph();
  const { outerRadius, setupMicEffect, teardownMicEffect } = useMicEffect();

  const micSensitivity = ref<number>(5);
  const socketId = ref<string>('');
  const events = ref<MicProcessEvents>({
    onmessage: null,
    onmessageerror: null,
  });

  /**
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Media_Capture_and_Streams_API/Constraints
   */
  const audioConstraints = computed(() => {
    const { os } = getBrowserAndDeviceInfo();
    return {
      echoCancellation: false,
      noiseSuppression: os === 'ios',
      autoGainControl: false,
      channelCount: 1,
    } satisfies MediaTrackConstraints;
  });

  const {
    enabled: isMicOn,
    stream,
    start,
    stop,
  } = useDisplayMedia({
    video: { width: 1, height: 1, frameRate: 1 },
    audio: audioConstraints.value,
  });

  const { analyzeMicGain } = useMicAnalyzer(isMicOn, {
    noiseFloor: DEFAULT_NOISE_FLOOR_CONFIG,
    clipping: DEFAULT_CLIPPING_CONFIG,
    noiseFloorHintBoxText: 'too_far_system_audio_mic',
    clippingHintBoxText: 'too_near_system_audio_mic',
  });

  const initStore = async (options: SystemAudioMicrophoneStoreOptions = {} as SystemAudioMicrophoneStoreOptions) => {
    socketId.value = options.socketId ?? '';
    events.value.onmessage = options.events?.onmessage ?? null;
    events.value.onmessageerror = options.events?.onmessageerror ?? null;

    await stopMic();
  };

  const startMic = async (): Promise<void> => {
    await clearMic();

    micGraphRenderQueue.add(async () => {
      try {
        await start();

        if (!stream.value) {
          throw new Error('Stream is unavailable after start.');
        }

        isMicOn.value = true;
        await initMicGraph();
        controlMicGain(micSensitivity.value);
      } catch {
        toastStore.error(t('reject_mic'));
        if (events.value.onmessageerror) {
          events.value.onmessageerror();
        }
        await stopMic();
      }
    });
  };

  const stopMic = async (): Promise<void> => {
    stop();
    await clearMic();
  };

  const clearMic = async (): Promise<void> => {
    micGraphRenderQueue.add(async () => {
      await teardownMicGraph();
      teardownMicEffect();
    });
  };

  const initMicGraph = async (): Promise<void> => {
    if (!stream.value) return;

    try {
      await setupMicGraph({
        stream,
        events: {
          onmessage: handleMicMessage,
          onmessageerror: handleMicMessageError,
        },
      });
      setupMicEffect(micGraph);
    } catch {
      toastStore.warn(t('error_mic'));
      await stopMic();
      if (events.value.onmessageerror) {
        events.value.onmessageerror();
      }
    }
  };

  const handleMicMessage = (e: MessageEvent<Float32Array>) => {
    if (isMicOn.value && events.value.onmessage) {
      events.value.onmessage(e);
      analyzeMicGain(e.data, true);
    }
  };

  const handleMicMessageError = async () => {
    toastStore.warn(t('unstable_mic'));
    await stopMic();
  };

  const controlMicGain = (sensitivity: number): void => {
    micSensitivity.value = sensitivity;

    if (micGraph.value) {
      micGraph.value.gain.gain.value = truncateToDecimal(MIC_INITIAL_GAIN * micSensitivity.value * VOLUME_RATIO);
    }
  };

  watch(isMicOn, (isOn) => {
    toastStore.info(isOn ? t('toast_mic_on') : t('toast_mic_off'), 'MIC');
  });

  return {
    /* states */
    isMicOn,
    socketId,
    micSensitivity,
    outerRadius,
    /* actions */
    initStore,
    startMic,
    stopMic,
    controlMicGain,
  };
});
